import { useState, type KeyboardEvent } from "react";
import { cn } from "../lib/cn";
import { ChartTableFallback, ChartTooltip, SvgFrame, defaultFormat, useChartTooltip } from "./chart-base";

/** Discrete flat brand steps (busiest hours), lowest → highest. */
export const HEATMAP_STEPS = [
  { fill: "fill-sunken", swatch: "bg-sunken" },
  { fill: "fill-brand-50", swatch: "bg-brand-50" },
  { fill: "fill-brand-100", swatch: "bg-brand-100" },
  { fill: "fill-brand-200", swatch: "bg-brand-200" },
  { fill: "fill-brand-300", swatch: "bg-brand-300" },
  { fill: "fill-brand-500", swatch: "bg-brand-500" },
  { fill: "fill-brand-700", swatch: "bg-brand-700" },
] as const;

const CELL = 20;
const GAP = 3;
const LABEL_W = 36;
const HEADER_H = 16;

/** Maps a value to a step: 0 → step 0, else evenly across steps 1..n-1 relative to max. */
export function heatLevel(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  const steps = HEATMAP_STEPS.length - 1;
  return Math.min(steps, Math.max(1, Math.ceil((value / max) * steps)));
}

export interface HeatmapProps {
  title: string;
  description?: string;
  /** Row labels, e.g. Mon…Sun. */
  rows: readonly string[];
  /** Column labels, e.g. "12 AM"…"11 PM" (used in names/tooltips). */
  columns: readonly string[];
  /** Short tick labels drawn over columns; "" to skip a column. Defaults to every 3rd column label. */
  columnTicks?: readonly string[];
  /** values[row][column] */
  values: readonly (readonly number[])[];
  formatValue?: (value: number) => string;
  legendLabels?: { less: string; more: string };
  className?: string;
}

/**
 * 7×24 heatmap. One tab stop (roving tabindex): arrows move between cells, Home/End jump within a row.
 */
export function Heatmap({ title, description, rows, columns, columnTicks, values, formatValue = defaultFormat, legendLabels = { less: "Less", more: "More" }, className }: HeatmapProps) {
  const { tip, show, hide } = useChartTooltip();
  const [active, setActive] = useState({ r: 0, c: 0 });
  const max = Math.max(0, ...values.flat());
  const width = LABEL_W + columns.length * (CELL + GAP);
  const height = HEADER_H + rows.length * (CELL + GAP);
  const ticks = columnTicks ?? columns.map((label, i) => (i % 3 === 0 ? label : ""));

  const cellName = (r: number, c: number) => `${rows[r] ?? ""} ${columns[c] ?? ""}: ${formatValue(values[r]?.[c] ?? 0)}`;
  const reveal = (r: number, c: number) => {
    const x = LABEL_W + c * (CELL + GAP) + CELL / 2;
    const y = HEADER_H + r * (CELL + GAP);
    show({ x: (x / width) * 100, y: (y / height) * 100, text: cellName(r, c), index: r * columns.length + c });
  };

  const onKeyDown = (event: KeyboardEvent<SVGRectElement>) => {
    const moves: Record<string, { r: number; c: number }> = {
      ArrowRight: { r: active.r, c: Math.min(active.c + 1, columns.length - 1) },
      ArrowLeft: { r: active.r, c: Math.max(active.c - 1, 0) },
      ArrowDown: { r: Math.min(active.r + 1, rows.length - 1), c: active.c },
      ArrowUp: { r: Math.max(active.r - 1, 0), c: active.c },
      Home: { r: active.r, c: 0 },
      End: { r: active.r, c: columns.length - 1 },
    };
    const next = moves[event.key];
    if (!next) return;
    event.preventDefault();
    setActive(next);
    const svg = event.currentTarget.ownerSVGElement;
    svg?.querySelector<SVGRectElement>(`[data-cell="${next.r}-${next.c}"]`)?.focus();
  };

  return (
    <div className={className}>
      <div className="mb-3 flex items-center justify-end gap-1 text-[11px] text-fg-subtle">
        <span>{legendLabels.less}</span>
        {HEATMAP_STEPS.map((step) => (
          <span key={step.swatch} aria-hidden="true" className={cn("size-3 rounded-xs", step.swatch)} />
        ))}
        <span>{legendLabels.more}</span>
      </div>
      <div className="overflow-x-auto">
        <div className="relative min-w-[560px]">
          <SvgFrame title={title} description={description} viewBox={`0 0 ${width} ${height}`} onMouseLeave={hide}>
            {ticks.map((tick, c) =>
              tick ? (
                <text key={`t-${c}`} x={LABEL_W + c * (CELL + GAP)} y={10} className="fill-fg-subtle text-[9px]" aria-hidden="true">
                  {tick}
                </text>
              ) : null,
            )}
            {rows.map((row, r) => (
              <g key={row}>
                <text x={0} y={HEADER_H + r * (CELL + GAP) + CELL * 0.68} className="fill-fg-subtle text-[10px] font-semibold" aria-hidden="true">
                  {row}
                </text>
                {columns.map((column, c) => {
                  const isActive = active.r === r && active.c === c;
                  return (
                    <rect
                      key={column}
                      data-cell={`${r}-${c}`}
                      x={LABEL_W + c * (CELL + GAP)}
                      y={HEADER_H + r * (CELL + GAP)}
                      width={CELL}
                      height={CELL}
                      rx={3}
                      tabIndex={isActive ? 0 : -1}
                      role="img"
                      aria-label={cellName(r, c)}
                      className={cn(HEATMAP_STEPS[heatLevel(values[r]?.[c] ?? 0, max)]?.fill, "outline-none hover:stroke-primary hover:[stroke-width:2px] focus-visible:stroke-fg focus-visible:[stroke-width:2px]")}
                      onMouseEnter={() => {
                        reveal(r, c);
                      }}
                      onFocus={() => {
                        setActive({ r, c });
                        reveal(r, c);
                      }}
                      onBlur={hide}
                      onKeyDown={onKeyDown}
                    />
                  );
                })}
              </g>
            ))}
          </SvgFrame>
          <ChartTooltip tip={tip} />
        </div>
      </div>
      <ChartTableFallback
        caption={title}
        table={{ columns: ["", ...columns], rows: rows.map((row, r) => [row, ...columns.map((_, c) => formatValue(values[r]?.[c] ?? 0))]) }}
      />
    </div>
  );
}
